import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimesCircle } from "@fortawesome/free-solid-svg-icons/faTimesCircle";
import { getCaptureMode, getCaptureSteps } from "@redux/selectors/verification-step-config-selectors";
import { getNextCaptureStepUrl, getCaptureStepInstructions, getCaptureStepLabel } from "@utils/capture-step-util";
import { OrientationPreviewContainer } from "../common/orientation-preview-container/orientation-preview-container";
import { useLocation, useNavigate } from "react-router-dom";
import { navigateToUtil } from "@utils/navigation-util";
import { GenericCaptureContainer } from "../common/generic-capture-container/generic-capture-container";
import { QualityChecksContainer } from "../common/quality-checks-container/quality-checks-container";
import { PostCaptureButtonContainer } from "../common/post-capture-button-container/post-capture-button-container";
import { ImagePreviewContainer } from "../common/image-preview-container/image-preview-container";
import { AcuantDocumentProcessingValidationResult } from "@idw-types/common/acuant-document-processing-result";
import { CaptureStepTypes } from "@enums/capture-step-types";
import { getLanguageContent } from "@redux/selectors/language-selectors";
import { APValidationLivenessAssessmentResult } from "@idw-types/requests/ap-validation-liveness-assessment-result";
import styles from "./capture-step-page.less";

type CaptureStepPageProps = {
    captureStepType: CaptureStepTypes;
    showQualityAdviceMessage: boolean;
    resetCaptureStepValidationResults: () => void;
};

export const CaptureStepPage = (props: CaptureStepPageProps) => {
    const { captureStepType, showQualityAdviceMessage, resetCaptureStepValidationResults } = props;

    const navigate = useNavigate();
    const location = useLocation();
    const content = useSelector(getLanguageContent);
    const captureSteps = useSelector(getCaptureSteps);
    const captureMode = useSelector(getCaptureMode);

    const imageRef = useRef<HTMLImageElement>(null);
    const [imageUrl, setImageUrl] = useState(null);
    const [imagePreviewLoaded, setImagePreviewLoaded] = useState(false);
    const [showCaptureError, setShowCaptureError] = useState(false);
    const [acuantValidationResults, setAcuantValidationResults] = useState<AcuantDocumentProcessingValidationResult>(null);
    const [livenessValidationResults, setLivenessValidationResults] = useState<APValidationLivenessAssessmentResult>(null);
    const captureStep = captureSteps.find(captureStep => captureStep.type === captureStepType);

    useEffect(() => {
        return () => {
            if (imageUrl) {
                URL.revokeObjectURL(imageUrl);
            }
        }
    }, [imageUrl]);

    useEffect(() => {
        if (captureStep?.completed && captureStep?.data) {
            const nextStepUrl = getNextCaptureStepUrl(captureSteps);
            navigateToUtil(nextStepUrl, navigate, location.pathname);
        }
    }, [captureStep?.completed]);

    if (!captureStep) {
        navigateToUtil("/start", navigate, location.pathname);
        return null;
    }

    const resetCaptureStepPage = () => {
        setShowCaptureError(false);
        setImageUrl(null);
        setImagePreviewLoaded(false);
        resetCaptureStepValidationResults();
    };

    const qualityAdviceMessage = content.general.qualityAdviceMessage;

    return (
        <div className={styles.captureStepPage}>
            <h1 id="capture-label">{getCaptureStepLabel(captureStep.type, content)}</h1>
            {!imageUrl && (
                <>
                    <div className={styles.stepInstructions}>
                        <span dangerouslySetInnerHTML={{ __html: getCaptureStepInstructions(captureStep.type, content) }} />
                    </div>
                    {showCaptureError && captureStep.type === CaptureStepTypes.FACE && (
                        <div className={styles.captureErrorMessage}>
                            <FontAwesomeIcon icon={faTimesCircle} className={styles.errorIcon} />
                            <span>
                                {content.acuant.validation.passiveLivenessGenericErrorMessage}
                            </span>
                        </div>
                    )}
                    {showCaptureError && captureStep.type !== CaptureStepTypes.FACE && content.acuant.errorMessage && (
                        <div className={styles.captureErrorMessage}>
                            <FontAwesomeIcon icon={faTimesCircle} className={styles.errorIcon} />
                            <span>
                                {content.acuant.errorMessage}
                            </span>
                        </div>
                    )}
                    {!showCaptureError && (
                        <>
                            {showQualityAdviceMessage && (
                                <div className={styles.qualityAdviceMessage}>
                                    <span>{qualityAdviceMessage}</span>
                                </div>
                            )}
                            <OrientationPreviewContainer captureStep={captureStep} />
                        </>
                    )}
                    <GenericCaptureContainer
                        captureMode={captureMode}
                        captureStep={captureStep}
                        setShowCaptureError={setShowCaptureError}
                        setImageUrl={setImageUrl}
                        setAcuantValidationResults={setAcuantValidationResults}
                        setLivenessValidationResult={setLivenessValidationResults}
                        resetCaptureStepPage={resetCaptureStepPage}
                    />
                </>
            )}
            {imageUrl && (
                <>
                    <ImagePreviewContainer
                        captureStep={captureStep}
                        imageRef={imageRef}
                        imageUrl={imageUrl}
                        imagePreviewLoaded={imagePreviewLoaded}
                        setImageUrl={setImageUrl}
                        setImagePreviewLoaded={setImagePreviewLoaded}
                    />
                    <QualityChecksContainer
                        captureStep={captureStep}
                        imageRef={imageRef}
                        imageUrl={imageUrl}
                        imagePreviewLoaded={imagePreviewLoaded}
                        acuantValidationResults={acuantValidationResults}
                        livenessValidationResults={livenessValidationResults}
                    />
                    <PostCaptureButtonContainer
                        captureStep={captureStep}
                        imageUrl={imageUrl}
                        resetCaptureStepPage={resetCaptureStepPage}
                    />
                </>
            )}
        </div>
    );
};